import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnInit,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { Subject, takeUntil, tap } from 'rxjs';
import { SnackbarMessageService } from '@ngx-interact-ui/core';
import { TranslateService } from '@ngx-translate/core';
import { BuildingIntegrationService } from '../../services/building-integration.service';
import { AmplitudeService } from '../../services/amplitude.service';
import { ApplicationEventService } from '../../services/application-events.service';
import { environment } from '../../environments/environment';
import { IBuildingIntegration } from '../../interfaces/i-building-integration';
import { IntegrationType } from '../../enums/integration-type.enum';
import { DEFAULT_LANGUAGE } from '../../constants';
import { IBuildingIntegrationConfigFile } from '../../interfaces/i-building-integration-configfile';

@Component({
  selector: 'app-building-integration',
  templateUrl: './building-integration.component.html',
  styleUrls: ['./building-integration.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class BuildingIntegrationComponent implements OnInit {
  @Input() public projectCode: string;
  @Input() public projectId!: number;
  @Input() public projectName: string;
  @Input() public accessToken: string;
  @Input() public language!: string;
  @Input() public isProjectDownloadPaid!: boolean;
  @Input() public isAdmin!: boolean;
  @Input() public bacnetEnabledFlag!: boolean;
  public buildingData: IBuildingIntegration[] = [];
  public gatewayAPIbuildingData: IBuildingIntegration[] = [];
  public BACnetBuildingData: IBuildingIntegration[] = [];
  public preparingDownload = false;
  public preparingBACnetDownload = false;
  public showContextualMessage = false;
  public isEnablingBacnet = false;

  public buildingId: string;
  private unsubscribe$ = new Subject<void>();

  get buildingIntegrations() {
    return this.buildingIntegrationService.state;
  }

  constructor(
    private buildingIntegrationService: BuildingIntegrationService,
    private snackbarMessageService: SnackbarMessageService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
    private readonly host: ElementRef,
    private readonly amplitudeService: AmplitudeService,
    private readonly applicationEventService: ApplicationEventService
  ) { }

  public ngOnInit(): void {
    this.translate.setDefaultLang(DEFAULT_LANGUAGE);
    this.translate.use(DEFAULT_LANGUAGE);
    this.initData();

  }

  public ngOnChanges(changes: SimpleChanges) {
    if (this.projectCode && this.accessToken) {
      this.host.nativeElement.dispatchEvent(
        new CustomEvent('showLoader', {
          detail: { showLoader: true },
          bubbles: true,
          composed: true,
        })
      );
      this.getBuildingIntegrations();
    }
  }

  public ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    // Trigger change detection on window resize to ensure responsive behavior
    this.cdr.detectChanges();
  }

  /**
   * Download Project JSON Data for gatewayAPI
   */
  public downloadProjectData() {
    const event = this.applicationEventService.clickOnTheDownloadIconInTheProjectBuildingIntegrationsPage(this.projectId);
    this.amplitudeService.logEvent(event.name, event.properties);
    this.preparingDownload = true;
    this.buildingIntegrationService
      .fetchProjectJsonData(this.projectId.toString())
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (res: any) => {
          const blob = new Blob([new Uint8Array(res['data'])]);
          const downloadLink = document.createElement('a');
          const objectUrl = window.URL.createObjectURL(blob);

          downloadLink.href = objectUrl;
          downloadLink.download = this.projectName + '.json';
          downloadLink.click();
          window.URL.revokeObjectURL(objectUrl);
          this.preparingDownload = false;
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.preparingDownload = false;
          this.translate
            .get('building_integrations.BUILDING_DOWNLOAD_FAILED')
            .subscribe((text) => {
              this.snackbarMessageService.warning({
                message: text,
              });
            });
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Download BACnet Integration Configuration File
   */
  public downloadBACnetProjectData() {
    const event = this.applicationEventService.clickOnTheDownloadOptionInCertificatesTable(this.projectId);
    this.amplitudeService.logEvent(event.name, event.properties);
    this.preparingBACnetDownload = true;
    this.buildingIntegrationService.fetchIntegrationConfigurationFile(this.projectCode, IntegrationType.BACNET, this.accessToken)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (res: IBuildingIntegrationConfigFile) => {
          this.preparingBACnetDownload = false;
          // Extract the file URL from the response
          const fileUrl = res?.configFile;
          if (fileUrl) {
            // Create a temporary anchor and trigger download
            const link = document.createElement('a');
            link.href = fileUrl;
            link.target = '_blank'; // optional, for S3 signed URLs
            link.download = ''; // let the browser use the filename from the URL
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.preparingBACnetDownload = false;
          this.handleBACnetDownloadError(error?.errors?.code);
          this.cdr.markForCheck();
        }
      })
  }


  public getExportIconUrl(): string {
    return `${environment.webcomponent_endpoint}/assets/images/icons/export.svg`;
  }

  public getNotConnectedIconUrl(): string {
    return `${environment.webcomponent_endpoint}/assets/images/icons/not-connected.svg`;
  }

  public getConnectedIconUrl(): string {
    return `${environment.webcomponent_endpoint}/assets/images/icons/connected.svg`;
  }

  public onDownloadKey(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.downloadBACnetProjectData();
    }
  }

  public onDownloadProjectDataKey(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.downloadProjectData();
    }
  }

  /**
   * Enable BACnet Integration
   */
  public enableBacnet() {
    this.isEnablingBacnet = true;
    this.buildingIntegrationService.enableBACnetIntegration(this.projectCode, this.accessToken).pipe(
      tap(() => {
        this.getBuildingIntegrations();
        this.isEnablingBacnet = false;
        this.cdr.detectChanges();
      })
    ).subscribe({
      error: (error) => {
        this.isEnablingBacnet = false;
        this.handleEnableBACnetError(error?.errors?.code);
        this.cdr.markForCheck();
      },
    });
  }

  public getBuildingIntegrations() {
    this.buildingIntegrationService
      .getBuildingIntegrations(this.projectCode, this.accessToken)
      .subscribe({
        error: (error) => {
          this.host.nativeElement.dispatchEvent(
            new CustomEvent('showLoader', {
              detail: { showLoader: false },
              bubbles: true,
              composed: true,
            })
          );
          this.translate
            .get('building_integrations.BUILDING_INTEGRATION_FAILED')
            .subscribe((text) => {
              this.snackbarMessageService.warning({
                message: text,
              });
            });
        },
      });
  }

  public initData() {
    //Listen to observable for building integrations
    this.buildingIntegrations.buildingIntegrations$.subscribe((data: IBuildingIntegration[]) => {
      if (data) {
        this.buildingData = data;
        this.gatewayAPIbuildingData = Array.isArray(data) ? data.filter(integration => integration.integrationType === IntegrationType.GATEWAYAPI) : [];
        this.BACnetBuildingData = Array.isArray(data) ? data.filter(integration => integration.integrationType === IntegrationType.BACNET) : [];

        this.showContextualMessage = this.BACnetBuildingData.some((integration) => integration.configGenerationRequired === true);
        this.cdr.detectChanges();
        this.host.nativeElement.dispatchEvent(
          new CustomEvent('showLoader', {
            detail: { showLoader: false },
            bubbles: true,
            composed: true,
          })
        );
      }
    })
  }

  /**
   * Handle BACnet download errors with appropriate translated messages
   */
  private handleEnableBACnetError(errorCode: string): void {
    let translationKey: string;

    switch (errorCode) {
      case 'INVALID_REQUEST_ERROR':
        translationKey = 'building_integrations.INVALID_REQUEST_ERROR';
        break;
      case 'RESOURCE_ALREADY_EXISTS_ERROR':
        translationKey = 'building_integrations.RESOURCE_ALREADY_EXISTS_ERROR';
        break;
      case 'BUILDING_NOT_FOUND_ERROR':
        translationKey = 'building_integrations.BUILDING_NOT_FOUND_ERROR';
        break;
      case 'DB_UNEXPECTED_ERROR':
        translationKey = 'building_integrations.DB_UNEXPECTED_ERROR';
        break;
      case 'UNSUPPORTED_OPERATION_ERROR':
        translationKey = 'building_integrations.UNSUPPORTED_OPERATION_ERROR';
        break;
      default:
        translationKey = 'building_integrations.ENABLE_BACNET_FAILED';
        break;
    }

    this.translate.get(translationKey).subscribe((text) => {
      this.snackbarMessageService.warning({
        message: text,
      });
    });
  }


  /**
   * Handle BACnet download errors with appropriate translated messages
   */
  private handleBACnetDownloadError(errorCode: string): void {
    let translationKey: string;

    switch (errorCode) {
      case 'CONNECT_GATEWAY_ERROR':
        translationKey = 'building_integrations.CONNECT_GATEWAY_ERROR';
        break;
      case 'FILE_PROCESSING':
        translationKey = 'building_integrations.FILE_PROCESSING_ERROR';
        break;
      case 'INVALID_INTEGRATION_TYPE_ERROR':
        translationKey = 'building_integrations.INVALID_INTEGRATION_TYPE_ERROR';
        break;
      case 'BUILDING_INTEGRATION_NOT_FOUND_ERROR':
        translationKey = 'building_integrations.BUILDING_INTEGRATION_NOT_FOUND_ERROR';
        break;
      case 'BACNET_CONFIGURATION_FILE_NOT_FOUND_ERROR':
        translationKey = 'building_integrations.BACNET_CONFIGURATION_FILE_NOT_FOUND_ERROR';
        break;
      default:
        translationKey = 'building_integrations.BUILDING_DOWNLOAD_FAILED';
        break;
    }

    this.translate.get(translationKey).subscribe((text) => {
      this.snackbarMessageService.warning({
        message: text,
      });
    });
  }
}


